import {
    AccountMappingAccountItem,
    IAccount,
    IAccountMappingEmpty,
    IAccountMappingHeader,
    IAccountMappingSubFLI,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IAccountMappingTemplateGroupItemSubtotal,
    IFinStatements,
    IManualAddedOrUpdatedAccount,
    ItemType,
    SortedMappingItem,
} from '@modeso/types__aumico-lib-annualreport-be';
import { SortedItem } from '../../../../../../../../aumico-lib-annualreport-fe/src/lib/helpers/datev.excel.helper';
import { FcpTreeItemType } from '../models/fcp.model';
import { FcpTreeItem } from '../models/fcpTreeItem.model';

export class FcpTreeItemMapper {
    public static flattenTree(tree: FcpTreeItem[]): FcpTreeItem[] {
        const result: FcpTreeItem[] = [];

        const flatten = (item: FcpTreeItem) => {
            result.push(item); // Add current item without its children
            item.children.forEach(flatten); // Recursively process each child
        };

        tree.forEach(flatten); // Process each root item in the tree
        return result;
    }

    public static filterItemsByType(items: FcpTreeItem[], type: FcpTreeItemType): FcpTreeItem[] {
        return items.filter((item) => item.type === type);
    }

    public static mapFinstatementToTreeItems(inputFinStatements: IFinStatements[]): FcpTreeItem[] {
        if (inputFinStatements.length > 0 && inputFinStatements[0].templateAndAccountsMapping.template) {
            const resultItems: FcpTreeItem[] = [];
            const fcpRootTreeItem: FcpTreeItem = {
                name: 'Root',
                openingValue: 0,
                closingValue: 0,
                tasks: [],
                checklistname: '',
                comments: [],
                documents: [],
                closingEntries: [],
                children: [],
                type: FcpTreeItemType.Root,
                active: false,
                uid: '**Root**',
                adjustedValue: 0,
                isDefaultChecklistDeleted: false,
            };
            resultItems.push(fcpRootTreeItem);

            // Map Balance
            const templateBalance = inputFinStatements[0].templateAndAccountsMapping.template;
            const fcpBalanceNavigationTreeItem: FcpTreeItem = {
                name: 'Balance',
                openingValue: 0,
                closingValue: 0,
                tasks: [],
                checklistname: '',
                comments: [],
                documents: [],
                closingEntries: [],
                children: [],
                type: FcpTreeItemType.Navigation,
                active: false,
                uid: 'Balance_MainNav',
                adjustedValue: 0,
                isDefaultChecklistDeleted: false,
            };
            fcpRootTreeItem.children.push(fcpBalanceNavigationTreeItem);

            templateBalance.group.sortedItems.forEach((item: SortedItem) => {
                // Map item...
                const fcpSubNavTreeItem: FcpTreeItem = {
                    name: item.title,
                    openingValue: item.calculatedOpeningBalance,
                    closingValue: item.calculatedClosingBalance,
                    tasks: [],
                    checklistname: '',
                    comments: [],
                    documents: [],
                    closingEntries: [],
                    children: [],
                    type: FcpTreeItemType.SubNavigation,
                    active: false,
                    uid: item.title + item.type + item.name + item.sortedItems.length,
                    adjustedValue: 0,
                    isDefaultChecklistDeleted: item.isDefaultChecklistDeleted,
                };
                fcpBalanceNavigationTreeItem.children.push(fcpSubNavTreeItem);
                if (item.sortedItems && item.sortedItems.length > 0) {
                    item.sortedItems.forEach((sortedItem) => {
                        const treeItem = this.mapSortedItemToTreeItem(sortedItem);
                        if (treeItem) {
                            fcpSubNavTreeItem.children.push(treeItem);
                        }
                    });
                }
            });

            // Map Income
            const templateIncome = inputFinStatements[1].templateAndAccountsMapping.template;
            const fcpIncomeNavigationTreeItem: FcpTreeItem = {
                name: 'Income',
                openingValue: 0,
                closingValue: 0,
                tasks: [],
                checklistname: '',
                comments: [],
                documents: [],
                closingEntries: [],
                children: [],
                type: FcpTreeItemType.Navigation,
                active: false,
                uid: 'Income_MainNav',
                adjustedValue: 0,
                isDefaultChecklistDeleted: false,
            };
            fcpRootTreeItem.children.push(fcpIncomeNavigationTreeItem);

            const fcpSubNavTreeItem: FcpTreeItem = {
                name: (templateIncome.group.groups[0] as IAccountMappingTemplateGroup)?.title,
                openingValue: 0,
                closingValue: 0,
                tasks: [],
                checklistname: '',
                comments: [],
                documents: [],
                closingEntries: [],
                children: [],
                type: FcpTreeItemType.SubNavigation,
                active: false,
                uid: 'Income_SubNav',
                adjustedValue: 0,
                isDefaultChecklistDeleted: false,
            };
            fcpIncomeNavigationTreeItem.children.push(fcpSubNavTreeItem);
            templateIncome.group.sortedItems.forEach((item: SortedItem) => {
                // Map item...
                const treeItem = this.mapSortedItemToTreeItem(item);
                if (treeItem) {
                    fcpSubNavTreeItem.children.push(treeItem);
                }
            });

            return resultItems;
        } else {
            return [];
        }
    }

    private static mapSortedItemToTreeItem(sortedItem: SortedMappingItem): FcpTreeItem | undefined {
        const fcpTreeItem: FcpTreeItem = {
            name: '?',
            openingValue: sortedItem.calculatedOpeningBalance,
            closingValue: sortedItem.calculatedClosingBalance,

            tasks: [],
            checklistname: '',
            comments: [],
            documents: [],
            closingEntries: [],
            children: [],
            type: FcpTreeItemType.TestItem,
            active: false,
            uid: '',
            adjustedValue: 0,
            isDefaultChecklistDeleted: false,
        };

        if (this.isTemplateGroupItemSubtotal(sortedItem)) {
            // Skip
            return undefined;
        } else if (this.isTemplateGroupItemFLI(sortedItem)) {
            // Handle IAccountMappingTemplateGroupItemFLI
            const fli = sortedItem as IAccountMappingTemplateGroupItemFLI;
            fcpTreeItem.type = FcpTreeItemType.TestGroup;
            fcpTreeItem.name = fli.caption;
            fcpTreeItem.treeItemName = fli.name;
            fcpTreeItem.uid = fli.caption + fli.type + fli.name + fli.accounts.length;
            fli.accounts.forEach((item: AccountMappingAccountItem) => {
                const childItem = this.mapAccountItemToTreeItem(item);
                if (childItem) {
                    fcpTreeItem.children.push(childItem);
                }
            });
        } else if (this.isTemplateGroupItemEmpty(sortedItem)) {
            // Skip
            return undefined;
        } else if (this.isTemplateGroupItemHeader(sortedItem)) {
            // Skip
            return undefined;
        } else if (this.isTemplateGroup(sortedItem)) {
            // Handle IAccountMappingTemplateGroup
            const group = sortedItem as IAccountMappingTemplateGroup;
            fcpTreeItem.type = FcpTreeItemType.Bundle;
            fcpTreeItem.name = group.title;

            fcpTreeItem.uid = group.title + group.totalName + group.name + group.items.length;
            sortedItem.sortedItems?.forEach((item) => {
                const childItem = this.mapSortedItemToTreeItem(item);
                if (childItem) {
                    fcpTreeItem.children.push(childItem);
                }
            });
        }

        return fcpTreeItem;
    }

    private static mapAccountItemToTreeItem(accountItem: AccountMappingAccountItem): FcpTreeItem | null {
        const fcpTreeItem: FcpTreeItem = {
            name: '?',
            openingValue: 0,
            closingValue: 0,
            tasks: [],
            checklistname: '',
            comments: [],
            documents: [],
            closingEntries: [],
            children: [],
            type: FcpTreeItemType.TestItem,
            active: false,
            uid: '',
            adjustedValue: 0,
        };

        if (this.isAccountMappingSubFLI(accountItem)) {
            // Handle SUBFLI and OF_THAT_ITEM
            const bundle = accountItem;
            fcpTreeItem.type = FcpTreeItemType.Bundle;
            fcpTreeItem.name = bundle.caption;
            fcpTreeItem.uid = bundle.caption + bundle.type + bundle.name + bundle.accounts.length;

            accountItem.accounts?.forEach((item) => {
                const childItem = this.mapAccountItemToTreeItem(item);
                if (childItem) {
                    fcpTreeItem.children.push(childItem);
                }
            });
        } else if (this.isAccount(accountItem)) {
            // Handle Account
            const account = accountItem;
            fcpTreeItem.type = FcpTreeItemType.TestItem;
            fcpTreeItem.name = account.accountNumber + ' ' + account.name;
            fcpTreeItem.openingValue = account.calculatedOpeningBalance;
            fcpTreeItem.closingValue = account.calculatedClosingBalance;
            fcpTreeItem.adjustedValue = account.calculatedClosingBalance;
            fcpTreeItem.uid = account.type + account.accountNumber + account.name;
        } else if (this.isAccountMappingEmpty(accountItem)) {
            // Skip
            return null;
        } else if (this.isAccountMappingHeader(accountItem)) {
            // Skip
            return null;
        } else if (this.isManualAddedOrUpdatedAccount(accountItem)) {
            // Handle IAccountMappingTemplateGroup
            const account = accountItem;
            fcpTreeItem.type = FcpTreeItemType.TestItem;
            fcpTreeItem.name = account.name;
            fcpTreeItem.uid =
                account.type +
                account.accountNumber +
                account.name +
                account.calculatedOpeningBalance +
                account.calculatedClosingBalance;
        }

        return fcpTreeItem;
    }

    private static isTemplateGroupItemSubtotal(
        item: SortedMappingItem,
    ): item is IAccountMappingTemplateGroupItemSubtotal {
        return (item as IAccountMappingTemplateGroupItemSubtotal).type === ItemType.SUB_TOTAL;
    }

    private static isTemplateGroupItemFLI(item: SortedMappingItem): item is IAccountMappingTemplateGroupItemFLI {
        return (item as IAccountMappingTemplateGroupItemFLI).type === ItemType.FLI;
    }

    private static isTemplateGroupItemEmpty(item: SortedMappingItem): item is IAccountMappingEmpty {
        return (item as IAccountMappingEmpty).type === ItemType.EMPTY;
    }

    private static isTemplateGroupItemHeader(item: SortedMappingItem): item is IAccountMappingHeader {
        return (item as IAccountMappingHeader).type === ItemType.HEADER;
    }

    private static isTemplateGroup(item: SortedMappingItem): item is IAccountMappingTemplateGroup {
        return (item as IAccountMappingTemplateGroup).items !== undefined;
    }

    private static isAccountMappingSubFLI(item: AccountMappingAccountItem): item is IAccountMappingSubFLI {
        return (
            (item as IAccountMappingSubFLI).type === ItemType.SUB_FLI ||
            (item as IAccountMappingSubFLI).type === ItemType.OF_THAT_ITEM
        );
    }

    private static isAccountMappingEmpty(item: AccountMappingAccountItem): item is IAccountMappingEmpty {
        return (item as IAccountMappingEmpty).type === ItemType.EMPTY;
    }

    private static isAccountMappingHeader(item: AccountMappingAccountItem): item is IAccountMappingHeader {
        return (item as IAccountMappingHeader).type === ItemType.HEADER;
    }

    private static isAccount(item: AccountMappingAccountItem): item is IAccount {
        return (item as IAccount).accountNumber !== undefined;
    }

    private static isManualAddedOrUpdatedAccount(
        item: AccountMappingAccountItem,
    ): item is IManualAddedOrUpdatedAccount {
        return (item as IManualAddedOrUpdatedAccount).ignoreFliSign !== undefined;
    }

    public static findSortedItemByUid(finStatementsArray: IFinStatements[], uid: string): SortedItem | null {
        // Iterate over each financial statement in the array
        for (const finStatements of finStatementsArray) {
            const { sortedItems } = finStatements.templateAndAccountsMapping.template.group;
            const foundItem = this.searchSortedItems(sortedItems, uid);
            if (foundItem) {
                return foundItem;
            }
        }
        return null; // Return null if no item with the given UID is found
    }
    public static findSortedItemByName(finStatementsArray: IFinStatements[], name: string): SortedItem | null {
        // Iterate over each financial statement in the array
        for (const finStatements of finStatementsArray) {
            const { sortedItems } = finStatements.templateAndAccountsMapping.template.group;
            const foundItem = this.searchSortedItemsByName(sortedItems, name);
            if (foundItem) {
                return foundItem;
            }
        }
        return null; // Return null if no item with the given UID is found
    }

    private static generateUID(item: SortedItem): string {
        if (this.isTemplateGroupItemFLI(item)) {
            const fli = item as IAccountMappingTemplateGroupItemFLI;
            return fli.caption + fli.type + fli.name + fli.accounts.length;
        } else if (this.isTemplateGroupItemSubtotal(item)) {
            const subtotal = item as IAccountMappingTemplateGroupItemSubtotal;
            return subtotal.type + subtotal.name + 0;
        } else if (this.isTemplateGroup(item)) {
            const group = item as IAccountMappingTemplateGroup;
            return group.title + group.totalName + group.name + group.items.length;
        } else {
            return item.title + item.type + item.name + (item.sortedItems ? item.sortedItems.length : 0);
        }
    }

    private static searchSortedItems(items: SortedItem[], uid: string): SortedItem | null {
        for (const item of items) {
            // Generate the UID based on the logic used in mapFinstatementToTreeItems
            const itemUid = this.generateUID(item);

            if (itemUid === uid) {
                return item; // Return the matching item
            }

            if (item.sortedItems && item.sortedItems.length > 0) {
                const found = this.searchSortedItems(item.sortedItems, uid);
                if (found) {
                    return found; // Return the found item from the recursive search
                }
            }
        }
        return null; // Return null if no item is found in this level of recursion
    }
    private static searchSortedItemsByName(items: SortedItem[], name: string): SortedItem | null {
        for (const item of items) {
            if (item['name'] === name) {
                return item; // Return the matching item
            }

            if (item.sortedItems && item.sortedItems.length > 0) {
                const found = this.searchSortedItemsByName(item.sortedItems, name);
                if (found) {
                    return found; // Return the found item from the recursive search
                }
            }
        }
        return null; // Return null if no item is found in this level of recursion
    }

    public static sumAccounts(sortedItem: SortedItem): { totalOpeningBalance: number; totalClosingBalance: number } {
        let totalOpeningBalance = 0;
        let totalClosingBalance = 0;

        function sumAccountsRecursively(item: SortedItem) {
            // Sum balances for accounts in the current item
            if (item.accounts) {
                for (const account of item.accounts) {
                    totalOpeningBalance += isNaN(account.calculatedOpeningBalance)
                        ? 0
                        : account.calculatedOpeningBalance;
                    totalClosingBalance += isNaN(account.calculatedClosingBalance)
                        ? 0
                        : account.calculatedClosingBalance;
                }
            }

            // Recursively sum balances for child sorted items
            if (item.sortedItems && item.sortedItems.length > 0) {
                for (const childItem of item.sortedItems) {
                    sumAccountsRecursively(childItem);
                }
            }
        }

        // Start the recursive sum from the provided sortedItem
        sumAccountsRecursively(sortedItem);

        return { totalOpeningBalance, totalClosingBalance };
    }
}
